// Google Analytics 4 (gtag.js) helpers for the Lemon Pros SPA.
// The base Google tag is loaded once in public/index.html with:
//   - GA4 G-PBB3G61CXB   (send_page_view:false — page views are sent manually here)
//   - Google Ads AW-318021992 (auto-tagging / GCLID + the form-submit conversion below)
import { useEffect, useRef, useState } from 'react';
import { recordCallClick } from '@/lib/tracking';
const GA4_ID = 'G-PBB3G61CXB';
// Google Ads "Lead Form Submit" conversion label (Event snippet send_to value).
const ADS_LEAD_CONVERSION = 'AW-318021992/QndSCIqez8EcEOjC0pcB';
// Google Ads "Click to call" conversion label (fires when a phone link is tapped).
const ADS_CALL_CONVERSION = 'AW-318021992/I_x1CMbE2cEcEOjC0pcB';

function gtagSafe(...args) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag(...args);
}

// Google Ads Call Conversion — phone-number swap.
// Registers the specific Google Ads "Call from website" conversion action AND
// tells Google which number on the page to swap with a Google forwarding
// number for visitors that arrived from a Google Ads click. Idempotent — safe
// to call on every render. Each landing page passes its OWN conversion label
// (English / Spanish / Display etc.) + the number displayed on that page.
export function registerPhoneConversionSwap({ conversionLabel, phone }) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
  }
  const digits = String(phone || '').replace(/\D/g, '').slice(-10);
  if (digits.length !== 10 || !conversionLabel) return;
  const phoneConvNumber = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  try {
    window.gtag('config', conversionLabel, { phone_conversion_number: phoneConvNumber });
  } catch (e) { /* never let analytics break the page */ }
}

// Map of every trackable displayed phone number → its Google Ads
// "Call from website" conversion action. Used by SiteHeader (and any other
// component that renders a persistent phone) to auto-register the correct
// pixel based on WHICH number is currently on the page. Adding a new page
// with a new number just requires one line here.
const PHONE_TO_CONVERSION_LABEL = {
  '8443358911': 'AW-318021992/2t9QCJ-pwtQcEOjC0pcB', // English  (/pa, /, /flow, /thank-you EN)
  '8665243722': 'AW-318021992/aNXQCN-1g9UcEOjC0pcB', // Spanish  (/spa, /sp, /flow, /thank-you ES)
  '8446181036': 'AW-318021992/B06_CIvKyNUcEOjC0pcB', // Display  (/pad)
  '8332409312': 'AW-318021992/ZfC7CPveytUcEOjC0pcB', // Demand Gen EN (/dg)
  '8338681802': 'AW-318021992/2AGnCKeiydUcEOjC0pcB', // Demand Gen ES (/dgs)
};

// Auto-picks the right conversion label based on the displayed phone number.
// Safe no-op if the number isn't tracked (nothing gets registered).
export function registerPhoneConversionSwapForNumber(phone) {
  const digits = String(phone || '').replace(/\D/g, '').slice(-10);
  const conversionLabel = PHONE_TO_CONVERSION_LABEL[digits];
  if (!conversionLabel) return;
  registerPhoneConversionSwap({ conversionLabel, phone });
}

// If a visitor arrived from a Google Ads click, gtag asynchronously swaps the
// on-page phone number (text + tel: href) with a Google forwarding number
// AFTER the initial paint — visible as a brief number flicker. This hook
// keeps the phone hidden (space reserved via opacity) until the swap either
// (a) lands — detected via MutationObserver on the phone element, or
// (b) safety-times out (~900ms) — so if Google never swaps (or the visitor
//     didn't come from Ads) the number reveals promptly and never stays hidden.
// Visitors without an Ads click ID / cookie skip hiding entirely.
function hasGoogleAdsClickIdentity() {
  if (typeof window === 'undefined') return false;
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.has('gclid') || p.has('gbraid') || p.has('wbraid')) return true;
    // gtag persists the click id into _gcl_aw / _gcl_dc after the first hit
    // so subsequent internal navigations still trigger the swap.
    const cookie = typeof document !== 'undefined' ? document.cookie || '' : '';
    if (/(?:^|; )_gcl_(aw|dc)=/.test(cookie)) return true;
  } catch (e) { /* fall through */ }
  return false;
}

export function useAdsPhoneSwapVisibility() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(() => !hasGoogleAdsClickIdentity());

  useEffect(() => {
    if (visible) return;
    let mounted = true;
    let observer;
    const reveal = () => { if (mounted) setVisible(true); };
    if (ref.current && typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(reveal);
      observer.observe(ref.current, { childList: true, characterData: true, subtree: true });
    }
    // Safety net: never leave the number invisible for more than ~900ms.
    const timeout = setTimeout(reveal, 900);
    return () => {
      mounted = false;
      if (observer) observer.disconnect();
      clearTimeout(timeout);
    };
  }, [visible]);

  return { ref, visible };
}

// Manual SPA page view — scoped to GA4 so it never reaches the Ads tag.
export function trackPageView(path) {
  gtagSafe('event', 'page_view', {
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
    page_path: path,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
    send_to: GA4_ID,
  });
  // Mirror the SPA route change to OpenAI Ads so visits are tracked
  // on every page, not just the initial HTML load.
  if (typeof window !== 'undefined' && typeof window.oaiq === 'function') {
    try { window.oaiq('measure', 'page_view'); } catch (e) { /* noop */ }
  }
}

// GA4 lead conversion (no PII). Fired on the Thank-You page after a real submit.
// Guarded against rapid duplicate fires (e.g. React StrictMode double-mount in dev)
// so a single completed lead is never counted twice.
let _lastLeadAt = 0;
export function trackGenerateLead(params = {}) {
  const now = Date.now();
  if (now - _lastLeadAt < 3000) return;
  _lastLeadAt = now;
  gtagSafe('event', 'generate_lead', { ...params, send_to: GA4_ID });
  // Mirror the lead to OpenAI Ads measurement pixel. event_id matches the
  // server-side CAPI call for dedup. value is wrapped in `type: contents`
  // per OpenAI's conversion-tracking schema.
  if (typeof window !== 'undefined' && typeof window.oaiq === 'function') {
    try {
      const eventId = params.transaction_id || params.event_id ||
        `lead_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const payload = { type: 'contents' };
      if (typeof params.value === 'number') payload.amount = Math.round(params.value * 100);
      payload.currency = params.currency || 'USD';
      window.oaiq('measure', 'lead_created', payload, { event_id: eventId });
    } catch (e) { /* noop */ }
  }
}

// Google Ads conversion — fired on the Thank-You page after a real submit.
// Tells Google Ads "this ad click converted" (uses the GCLID from auto-tagging).
// Guarded against duplicate fires (React StrictMode double-mount / re-render).
//
// Enhanced Conversions:
//   • Pass `enhancedData` (raw email/phone/first/last) to attach hashed
//     user-provided data to the tag. Google hashes on the client before send,
//     so no PII leaves the browser in plaintext.
//   • Pass `transactionId` so the tag's order id matches the server-side
//     offline API upload — Google's diagnostics require both sides to match.
let _lastAdsConvAt = 0;
export function trackAdsConversion({ value, currency = 'USD', transactionId, enhancedData } = {}) {
  const now = Date.now();
  if (now - _lastAdsConvAt < 3000) return;
  _lastAdsConvAt = now;
  // Attach hashed user-provided data to the tag BEFORE the conversion fires.
  // Google Tag SDK will SHA-256 these fields client-side before transmission.
  if (enhancedData && typeof enhancedData === 'object') {
    const ud = {};
    if (enhancedData.email) ud.email = String(enhancedData.email).trim().toLowerCase();
    if (enhancedData.phone) ud.phone_number = String(enhancedData.phone).replace(/\D/g, '');
    const addr = {};
    if (enhancedData.first_name) addr.first_name = String(enhancedData.first_name).trim().toLowerCase();
    if (enhancedData.last_name) addr.last_name = String(enhancedData.last_name).trim().toLowerCase();
    if (enhancedData.city) addr.city = String(enhancedData.city).trim().toLowerCase();
    if (enhancedData.state) addr.region = String(enhancedData.state).trim().toLowerCase();
    if (enhancedData.zip) addr.postal_code = String(enhancedData.zip).trim();
    if (Object.keys(addr).length) ud.address = addr;
    if (Object.keys(ud).length) gtagSafe('set', 'user_data', ud);
  }
  const payload = { send_to: ADS_LEAD_CONVERSION };
  if (typeof value === 'number') payload.value = value;
  if (currency) payload.currency = currency;
  if (transactionId) payload.transaction_id = transactionId;
  gtagSafe('event', 'conversion', payload);
}

// Google Ads "Click to call" conversion — fired when a visitor taps a phone link.
// Uses Google's recommended event_callback pattern: we hold the tel: navigation
// until the conversion ping is sent, then hand off to the dialer. Without this,
// the mobile dialer backgrounds the page and cancels the in-flight ping, so the
// call conversion silently under-reports / "doesn't fire".
let _lastCallConvAt = 0;
export function trackPhoneCallConversion(e) {
  const anchorEl = e && e.currentTarget && e.currentTarget.getAttribute ? e.currentTarget : null;
  const telHref = anchorEl ? anchorEl.getAttribute('href') : null;
  // Log the tap to our backend (source_page + dialed number) for exact per-page
  // call attribution. Fires regardless of whether gtag is available.
  recordCallClick(telHref);

  // Meta Pixel "Contact" event — mirrors Google Ads call conversion so Meta
  // sees a phone-tap as a conversion signal too. eventID is stable per tap
  // so no double-count if the visitor accidentally double-taps.
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      const num = String(telHref || '').replace(/\D/g, '').slice(-10);
      const evtId = `contact-${num}-${Math.floor(Date.now() / 1000)}`;
      window.fbq('track', 'Contact', { content_name: 'phone_tap', phone: num }, { eventID: evtId });
      // Mirror the phone tap to OpenAI Ads so inbound-call conversions are
      // tracked next to Meta Contact and Google Ads call conversion.
      if (typeof window.oaiq === 'function') {
        try { window.oaiq('measure', 'contact', { type: 'contents' }, { event_id: evtId }); }
        catch (e) { /* noop */ }
      }
    }
  } catch (err) { /* never block the call */ }

  const now = Date.now();
  if (now - _lastCallConvAt < 1500) return; // let default tel: navigation proceed
  _lastCallConvAt = now;

  // gtag unavailable → don't block the call, just let the link work.
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  const url = telHref;

  if (url) {
    e.preventDefault(); // must be synchronous
    let navigated = false;
    const go = () => { if (!navigated) { navigated = true; window.location = url; } };
    setTimeout(go, 700); // fallback if event_callback never fires
    window.gtag('event', 'conversion', {
      send_to: ADS_CALL_CONVERSION,
      value: 1.0,
      currency: 'USD',
      event_callback: go,
    });
  } else {
    window.gtag('event', 'conversion', { send_to: ADS_CALL_CONVERSION, value: 1.0, currency: 'USD' });
  }
}
